/**
 * The tracker vocabularies, as the web reads them.
 *
 * `trackerImportContract.json` is a BYTE-IDENTICAL copy of the API's
 * `services/system/tracker-imports/tracker-import-contract.json` (pinned by
 * `trackerImportContract.parity.test.ts`). It is the one source of:
 *   - each import kind's fields (what the importer's mapping step offers);
 *   - the words a contract status, a value period, a checklist item's status
 *     and an onboarding stage can be — the same words the API validates, so a
 *     select here can only ever offer what the server accepts.
 *
 * Pure (no React, no MUI) so it runs in vitest's node environment.
 */
import contractJson from "./trackerImportContract.json";

export type TrackerFieldType =
  | "text"
  | "client"
  | "invoice"
  | "person"
  | "date"
  | "money"
  | "integer"
  | "boolean"
  | "enum";

export interface TrackerFieldDef {
  key: string;
  label: string;
  type: TrackerFieldType;
  required?: boolean;
  maxLength?: number;
  scale?: number;
  max?: number;
  unit?: "months" | "days";
  enum?: string;
  aliases: string[];
}

export type TrackerImportKind = "ar-invoices" | "ar-payments" | "contracts" | "onboarding";

export interface TrackerKindDef {
  key: TrackerImportKind;
  label: string;
  description: string;
  target: string;
  fields: TrackerFieldDef[];
}

export interface VocabularyValue {
  key: string;
  label: string;
}

export interface TrackerImportContract {
  version: number;
  sourceSystems: VocabularyValue[];
  kinds: TrackerKindDef[];
  enums: Record<string, { values: VocabularyValue[]; synonyms: Record<string, string> }>;
}

export const TRACKER_CONTRACT = contractJson as unknown as TrackerImportContract;

export const TRACKER_KINDS: readonly TrackerKindDef[] = TRACKER_CONTRACT.kinds;

export const kindDef = (kind: string): TrackerKindDef | undefined =>
  TRACKER_KINDS.find((k) => k.key === kind);

/** A vocabulary's values in order, or none. */
export const vocabulary = (name: string): readonly VocabularyValue[] =>
  TRACKER_CONTRACT.enums[name]?.values ?? [];

/** A key's label in its vocabulary; the key itself when it is not one (a retired word still reads). */
export const labelOf = (name: string, key: string | null | undefined): string => {
  if (!key) return "";
  return vocabulary(name).find((v) => v.key === key)?.label ?? key;
};

export const CONTRACT_STATUSES = vocabulary("contractStatus");
export const VALUE_PERIODS = vocabulary("valuePeriod");
export const TASK_STATUSES = vocabulary("taskStatus");
export const ONBOARDING_STAGES = vocabulary("onboardingStage");

/** Checklist statuses still to do. */
export const OPEN_TASK_STATUSES: ReadonlySet<string> = new Set(["todo", "in_progress", "blocked"]);
